<script setup lang="ts">
import { computed } from 'vue'
import type { DropdownMenuContentProps } from 'reka-ui'
import { orderBy } from 'lodash'
import draggable from 'vuedraggable'

import type { DropdownChecklistData } from './types'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuTrigger,
} from '@ds/primitives/dropdown-menu'
import DropdownChecklistItem from './DropdownChecklistItem.vue'
import DropdownMenuLabel from '@ds/primitives/dropdown-menu/DropdownMenuLabel.vue'

type Props = {
  label?: string
  data: DropdownChecklistData[]
  readonly?: boolean
  /** Off by default only for `singleSelect` — a set of mutually exclusive options has no order to save. */
  disallowDrag?: boolean
  singleSelect?: boolean
  side?: DropdownMenuContentProps['side']
  align?: DropdownMenuContentProps['align']
}

const {
  side = 'bottom',
  align = 'end',
  readonly = false,
  disallowDrag = false,
  singleSelect = false,
  label,
  ...props
} = defineProps<Props>()

const isOpen = defineModel<boolean>('open', { default: false })
const modelValue = defineModel<string[] | string>()

const showHandle = computed(() => !singleSelect && !disallowDrag && !readonly)

// Selected items float to the top, in the order the caller's `modelValue`
// already carries — dragging one further reorders that same array, so the
// display order and the persisted order are always the same thing.
const orderedData = computed({
  get() {
    if (singleSelect || disallowDrag) return props.data
    return orderBy(props.data, item => {
      const index = (modelValue.value as string[] | undefined)?.indexOf(item.value ?? '')
      return index === undefined || index === -1 ? Infinity : index
    })
  },
  set(value: DropdownChecklistData[]) {
    modelValue.value = orderBy(
      modelValue.value as string[],
      item => value.findIndex(v => v.value === item),
    )
  },
})

const handleUpdateChecked = (value: string | undefined, checked: boolean) => {
  if (!value) return
  const current = (modelValue.value as string[] | undefined) ?? []
  modelValue.value = checked
    ? [...new Set([...current, value])]
    : current.filter(item => item !== value)
}
</script>

<template>
  <DropdownMenu v-model:open="isOpen">
    <DropdownMenuTrigger as-child>
      <slot :is-open />
    </DropdownMenuTrigger>
    <DropdownMenuContent
      :side
      :align
      class="w-56"
    >
      <DropdownMenuLabel v-if="label">
        {{ label }}
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup
        v-if="singleSelect"
        :model-value="modelValue as string"
        @update:model-value="modelValue = $event as string"
      >
        <DropdownChecklistItem
          v-for="item in data"
          :key="item.value"
          :item
          single-select
          :readonly
        />
      </DropdownMenuRadioGroup>
      <draggable
        v-else
        v-model="orderedData"
        :disabled="disallowDrag || readonly"
        :animation="200"
        handle=".handle"
        item-key="value"
        tag="div"
        class="flex flex-col gap-px"
      >
        <template #item="{ element }">
          <DropdownChecklistItem
            :item="element"
            :checked="(modelValue as string[] | undefined)?.includes(element.value) ?? false"
            :show-handle="showHandle"
            :readonly
            @update:checked="handleUpdateChecked(element.value, $event)"
          />
        </template>
      </draggable>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
